"use client";

import { faPenRuler } from "@fortawesome/pro-solid-svg-icons";
import { Typography, VideoPlayer } from "@tosize/ui";
import { useEffect, useMemo, useRef } from "react";
import type SwiperCore from "swiper";
import { type SwiperClass, SwiperSlide } from "swiper/react";
import { StyledLink } from "~/components/Link";
import { useMedia } from "~/components/media/Provider";
import Image from "~/components/utilities/Image";
import { SwiperSlider } from "~/components/utilities/SwiperSlider";

export function MediaDialogDetail() {
	const {
		sortedSections,
		detailSection,
		detailIndex,
		viewMessage,
		projectMessage,
		setDetailSection,
		setDetailIndex,
		fetchMoreProjects,
	} = useMedia();

	const slides = useMemo(
		() =>
			sortedSections.flatMap((section) =>
				section.items.map((item) => ({ ...item, id: section.id })),
			),
		[sortedSections],
	);

	const index = useMemo(() => {
		const section = sortedSections.find((section) => section.id === detailSection);
		const previousSections = section
			? sortedSections.slice(0, sortedSections.indexOf(section))
			: undefined;
		const previousSlidesCount =
			previousSections?.reduce((previous, current) => previous + current.items.length, 0) ?? 0;
		const currentSlideIndex = section?.items.findIndex((_item, index) => index === detailIndex);

		return currentSlideIndex !== undefined ? previousSlidesCount + currentSlideIndex : undefined;
	}, [detailIndex, detailSection, sortedSections]);

	const swiperRef = useRef<SwiperCore>(undefined);

	useEffect(() => {
		if (index !== undefined) {
			swiperRef.current?.slideTo(index);
		}
	}, [index]);

	return (
		<>
			<SwiperSlider
				mousewheel
				onSwiper={(swiper: SwiperClass) => {
					swiperRef.current = swiper;

					if (index !== undefined) {
						swiper.slideTo(index);
					}
				}}
				onSlideChange={async (swiper) => {
					const currentSlide = slides.find((_slide, index) => index === swiper.activeIndex);
					const currentSection = sortedSections.find((section) => section.id === currentSlide?.id);
					const previousSections = currentSection
						? sortedSections.slice(0, sortedSections.indexOf(currentSection))
						: undefined;
					const previousSlidesCount =
						previousSections?.reduce((previous, current) => previous + current.items.length, 0) ??
						0;
					const detailIndex = swiper.activeIndex - previousSlidesCount;

					setDetailIndex(detailIndex);
					setDetailSection(currentSlide?.id);

					const reachedOneBeforeEndOfProjects =
						swiper.activeIndex >= previousSlidesCount + Number(currentSection?.items.length) - 2;

					if (currentSection?.id === "projects" && reachedOneBeforeEndOfProjects) {
						await fetchMoreProjects(detailIndex);
					}
				}}
				paginationClassName="fixed bottom-[var(--media-dialog-header-y,0px)] left-1/2 sm:static sm:bottom-0 sm:left-0"
			>
				{slides.map((item, index) => (
					<SwiperSlide key={index}>
						{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need for key events */}
						<div
							className="relative flex h-[calc(100dvh-var(--media-dialog-header-y,0px)-2rem)] w-full sm:h-[calc(100dvh-var(--media-dialog-header-y,0px)-3rem)]"
							onClick={(e) => {
								const element = e.currentTarget.firstElementChild;

								if (
									element instanceof HTMLIFrameElement &&
									element.style.pointerEvents === "none"
								) {
									element.style.pointerEvents = "unset";
									element.allow = "autoplay";
									const prefix = element.src.indexOf("?") === -1 ? "?" : "&";
									element.src = `${element.src + prefix}autoplay=1`;
								}
							}}
						>
							{item.type === "youtube" || item.type === "vimeo" ? (
								<VideoPlayer
									title={item.alt}
									variant={item.type}
									id={item.videoId}
									active={detailIndex === index}
									wrapperClassName="m-auto aspect-video max-h-full w-full max-w-full xl:h-full xl:w-auto"
								/>
							) : (
								<Image
									src={item.src ?? item.alternativeSrc ?? ""}
									alt={item.alt}
									fill
									style={{
										objectFit: "contain",
										objectPosition: "center",
									}}
								/>
							)}
						</div>

						{item.type === "project" && (
							<div className="absolute right-0 bottom-0 left-0 flex bg-gradient-to-t from-[rgba(70,70,70,0.7)] via-[rgba(70,70,70,0.57)] pt-8 pb-4 text-white sm:pt-12 sm:pb-8">
								<div className="container mt-auto flex flex-col items-start gap-4">
									<div className="flex w-full items-center gap-4">
										<Typography
											variant="h4"
											component="span"
											color="text-white"
											starticon={faPenRuler}
											className="truncate leading-tight"
										>
											{item.alt}
										</Typography>
										<hr className="grow" />
									</div>

									{!!item.description && (
										<Typography color="text-white" className="line-clamp-2 leading-tight">
											{item.description}
										</Typography>
									)}

									{!!item.url && (
										<StyledLink variant="outlined" color="white" href={item.url} target="_blank">
											{viewMessage} {projectMessage}
										</StyledLink>
									)}
								</div>
							</div>
						)}
					</SwiperSlide>
				))}
			</SwiperSlider>
		</>
	);
}
